import {
  Leaf, HeartHandshake, ShieldCheck, BookOpen, Users,
  MessageCircle, Sparkles, Compass,
} from "lucide-react";
import useSeo, { PROD_DOMAIN } from "../lib/seo";
import {
  TrustPage, TrustHero, TrustSection, IconCardGrid,
  ClosingCta, ReassuranceRibbon,
} from "../components/TrustLayout";

/**
 * /about — the "human" page. Tone: family-oriented, warm, calm,
 * wellness-guidance-first. NEVER says "buy now" or "shop our supplements".
 */
export default function AboutPage() {
  useSeo({
    title: "About Feel Greats — Guided wellness, not pressure",
    description:
      "We're a small wellness team helping you choose thoughtfully — not a supplement store. Personal mentors, calm guidance and an educational-first philosophy.",
    canonical: `${PROD_DOMAIN}/about`,
    ogType: "website",
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "AboutPage",
        "name": "About Feel Greats",
        "url": `${PROD_DOMAIN}/about`,
        "mainEntity": {
          "@type": "Organization",
          "name": "Feel Greats",
          "url": PROD_DOMAIN,
          "sameAs": [PROD_DOMAIN],
          "description":
            "Feel Greats is a guided wellness concierge platform — personal assessments, mentor support, and educational-first recommendations of clean Unicity products.",
        },
      },
    ],
  });

  return (
    <TrustPage testid="about-page">
      <TrustHero
        eyebrow="About Feel Greats"
        headline="Wellness that finally feels gentle."
        highlightWord="gentle"
        body="We started Feel Greats because too many people we love were drowning in supplements they didn't need and advice they didn't trust. We wanted to build the opposite: a calm, guided wellness experience where the right next step is always the smallest one."
        testid="about-hero"
      />

      {/* Why we exist */}
      <TrustSection
        eyebrow="Why we exist"
        title="The wellness internet is loud. We chose quiet."
        body="Most wellness brands shout. They promise miracles, transformations, and 30-day results. We don't. We believe the body changes slowly and that the right product matters less than the right rhythm. So Feel Greats is built around three convictions:"
        tone="default"
        testid="about-why"
      >
        <IconCardGrid
          cols={3}
          cards={[
            {
              icon: BookOpen,
              eyebrow: "Conviction 1",
              title: "Education before commerce.",
              body: "Every page should leave you a little smarter about your body — whether you buy anything or not.",
            },
            {
              icon: HeartHandshake,
              eyebrow: "Conviction 2",
              title: "Mentors before algorithms.",
              body: "A short voice note from a real human still beats the best recommendation engine ever built.",
            },
            {
              icon: ShieldCheck,
              eyebrow: "Conviction 3",
              title: "Safety before speed.",
              body: "If you're pregnant, on medication, or unsure — we'd rather slow down than push a sale.",
            },
          ]}
        />
      </TrustSection>

      {/* Our mission */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16" data-testid="about-mission">
        <p className="text-[11px] uppercase tracking-[0.22em] text-emerald-700 font-semibold">Our mission</p>
        <h2 className="font-display text-3xl sm:text-4xl text-emerald-950 font-light leading-tight mt-2 max-w-3xl">
          Help one million people feel quietly, consistently <em className="italic text-emerald-700">great</em>.
        </h2>
        <div className="mt-6 space-y-4 text-base text-emerald-900/85 leading-relaxed max-w-3xl">
          <p>
            We don't measure success in revenue or repeat orders. We measure it in the small, almost-boring victories — sleeping through the night, finally walking up the stairs without thinking about it, fitting into clothes that haven't fit in years, having the energy to be present with your kids after work.
          </p>
          <p>
            None of that comes from a single supplement. It comes from rhythm, hydration, sleep, and the right gentle support in the right season of life. Our job is to be the steady, patient guide that helps you find that rhythm — and to <em>stay in your life</em> long after the first purchase.
          </p>
        </div>
      </section>

      {/* Guided wellness philosophy */}
      <TrustSection
        eyebrow="Our philosophy"
        title="Guided wellness, not shopping."
        body="Choosing the wrong product is rarely the real problem. Choosing without guidance is."
        tone="amber"
        testid="about-philosophy"
      >
        <IconCardGrid
          cols={2}
          cards={[
            {
              icon: Compass,
              title: "We start with your story, not your basket.",
              body: "Before we ever mention a product, we ask about your sleep, your stress, your goals, your history. Your free assessment is the most important thing we do — it shapes everything that follows.",
            },
            {
              icon: Leaf,
              title: "We recommend less, not more.",
              body: "A wellness shelf with 14 supplements isn't progress — it's clutter. Most of our members start with one or two products and a habit, and that's enough.",
            },
            {
              icon: Users,
              title: "We never disappear after checkout.",
              body: "A real human checks in. We offer a WhatsApp mentor, monthly nudges, and the occasional voice note when we think you'd benefit from one. You're not on your own.",
            },
            {
              icon: MessageCircle,
              title: "We say 'this isn't for you' when it isn't.",
              body: "If your assessment suggests our products aren't a fit, we'll tell you. We'd rather have a small loyal community than a big disappointed one.",
            },
          ]}
        />
      </TrustSection>

      {/* Real support, not pressure */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16" data-testid="about-support">
        <p className="text-[11px] uppercase tracking-[0.22em] text-emerald-700 font-semibold">Real support, not pressure</p>
        <h2 className="font-display text-3xl sm:text-4xl text-emerald-950 font-light leading-tight mt-2 max-w-3xl">
          You're not closing a sale — you're starting a quiet relationship.
        </h2>
        <p className="mt-5 text-base text-emerald-900/85 leading-relaxed max-w-3xl">
          When someone joins Feel Greats, we treat it like meeting a new neighbour — not capturing a customer. There's a calm intake, a personalised report, and the option to chat with a wellness mentor on WhatsApp before you ever buy. After your first order, we check in at week 2, week 6, and quarterly — never with a "buy more" pitch. Just a "how are you actually feeling?"
        </p>
        <p className="mt-4 text-base text-emerald-900/85 leading-relaxed max-w-3xl">
          We'd rather hear that you took our advice and didn't need to buy anything than that you spent £200 on the wrong combo. That's the relationship.
        </p>
      </section>

      {/* Meet your mentors */}
      <TrustSection
        eyebrow="Your mentors"
        title="Real humans, not chatbots."
        body="A small, hand-picked team of certified wellness practitioners — based in the UK, supporting members worldwide. Available on WhatsApp during working hours, and via email any time."
        tone="white"
        testid="about-mentors"
      >
        <IconCardGrid
          cols={3}
          cards={[
            {
              icon: HeartHandshake,
              eyebrow: "Lead mentor · UK",
              title: "Holistic nutrition guidance.",
              body: "Certified in nutritional therapy with 9 years of one-on-one client work. Best for: weight, gut, energy, peri/menopause.",
            },
            {
              icon: HeartHandshake,
              eyebrow: "Support mentor · UK",
              title: "Habit-formation specialist.",
              body: "Trained in behavioural coaching, focused on small daily anchors. Best for: sleep, stress, sustainable routines.",
            },
            {
              icon: HeartHandshake,
              eyebrow: "Concierge · UK",
              title: "Order, subscription, refund care.",
              body: "The friendly first reply on most messages. Handles delivery, subscription pauses, and gentle handovers to a mentor.",
            },
          ]}
        />
      </TrustSection>

      {/* What happens after purchase */}
      <TrustSection
        eyebrow="After you buy"
        title="The journey actually starts here."
        body="Most brands stop talking once payment goes through. We're built to do the opposite."
        tone="amber"
        testid="about-after-purchase"
      >
        <IconCardGrid
          cols={2}
          cards={[
            { icon: Sparkles, title: "Day 1 — A short, warm welcome", body: "A real-human email (not an autoresponder) explaining what to expect in the first 14 days." },
            { icon: BookOpen, title: "Week 2 — First check-in", body: "A simple message: 'How's it going?' No agenda. Reply if you'd like to chat." },
            { icon: HeartHandshake, title: "Week 6 — Mentor nudge", body: "Most subtle shifts land in week 4-6. We share a small voice note with what to look for, and offer a 1-on-1 if helpful." },
            { icon: ShieldCheck, title: "Quarterly — Reassessment", body: "Your goals evolve. So should your support. We re-run the assessment with you each quarter and adjust." },
          ]}
        />
      </TrustSection>

      {/* Why wellness should feel simpler */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 text-center" data-testid="about-simpler">
        <p className="text-[11px] uppercase tracking-[0.22em] text-emerald-700 font-semibold">Why wellness should feel simpler</p>
        <h2 className="font-display text-3xl sm:text-4xl text-emerald-950 font-light leading-tight mt-2 max-w-3xl mx-auto">
          You don't need 14 supplements. You need one or two, and a steady hand.
        </h2>
        <p className="mt-5 text-base text-emerald-900/75 leading-relaxed max-w-2xl mx-auto">
          That's our entire pitch. If we can replace your supplement clutter with a calmer, smaller, more thoughtful shelf — and keep a real human on call — we've done our job.
        </p>
      </section>

      <ClosingCta
        eyebrow="Start when you're ready"
        title="Curious? Begin with the free assessment."
        body="Two minutes. No card. We'll suggest your most aligned next step — even if that step isn't one of our products."
      />
    </TrustPage>
  );
}
